import { ShieldCheck } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { QrPanel } from '@/components/shared/qr-panel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { OtpInput } from '@/components/ui/otp-input';
import { Skeleton } from '@/components/ui/skeleton';
import { useTotpSetupFlow } from '@/hooks/use-totp-setup-flow';

export interface TotpEnrollmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Plain-language reason this step is needed. */
  description?: string;
  /** Begin the QR setup as soon as the dialog opens. */
  autoStart?: boolean;
  /** When false, the dialog stays open until two-factor is on. */
  dismissible?: boolean;
  /** Called after recovery codes are saved and two-factor is on. */
  onEnrolled?: () => void;
}

export const TotpEnrollmentDialog = ({
  open,
  onOpenChange,
  description,
  autoStart = true,
  dismissible = true,
  onEnrolled,
}: TotpEnrollmentDialogProps) => {
  const { t } = useTranslation('security');
  const { t: tCommon } = useTranslation();
  const autoStartAttempted = useRef(false);

  const flow = useTotpSetupFlow({
    onEnabled: () => {
      onEnrolled?.();
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (!open) {
      autoStartAttempted.current = false;
      flow.reset();
      return;
    }

    if (flow.enabled && !flow.recoveryCodes) {
      onEnrolled?.();
      onOpenChange(false);
      return;
    }

    if (
      !autoStart ||
      autoStartAttempted.current ||
      flow.enabled ||
      flow.setupUrl ||
      flow.isStarting ||
      flow.statusLoading
    ) {
      return;
    }

    autoStartAttempted.current = true;
    flow.startSetup();
  }, [
    autoStart,
    flow.enabled,
    flow.isStarting,
    flow.recoveryCodes,
    flow.reset,
    flow.setupUrl,
    flow.startSetup,
    flow.statusLoading,
    onEnrolled,
    onOpenChange,
    open,
  ]);

  const handleOpenChange = (next: boolean) => {
    if (!next && !dismissible) return;
    onOpenChange(next);
  };

  const showCodeStep = Boolean(flow.setupUrl && !flow.recoveryCodes);
  const showRecoveryStep = Boolean(flow.recoveryCodes);
  const showPreparing = flow.isStarting && !flow.setupUrl;
  const showManualStart =
    !flow.setupUrl && !flow.isStarting && !flow.recoveryCodes && !autoStart;

  const canConfirm = flow.code.length === 6;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size="md"
        closeLabel={tCommon('action.close')}
        hideCloseButton={!dismissible}
        className="gap-0 overflow-visible p-0 sm:p-0"
      >
        <div className="border-b border-border bg-brand/[0.06] px-6 pt-6 pb-5">
          <div className="flex items-start gap-4 pr-8">
            <span
              className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand"
              aria-hidden
            >
              <ShieldCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-card-title leading-snug">
                {t('enrollmentRequired.title')}
              </DialogTitle>
              <DialogDescription className="mt-2 text-body-sm leading-relaxed">
                {description ?? t('enrollmentRequired.description')}
              </DialogDescription>
            </div>
          </div>
        </div>

        <DialogBody
          className="max-h-[min(70dvh,40rem)]"
          contentClassName="px-6 py-5"
        >
          {showPreparing ? (
            <div className="flex flex-col items-center gap-4 py-2">
              <Skeleton className="h-4 w-52 max-w-full" />
              <Skeleton className="size-[204px] rounded-xl" />
              <p className="text-body-sm text-fg-secondary">{t('enrollmentRequired.preparing')}</p>
            </div>
          ) : null}

          {showCodeStep ? (
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-body-sm text-fg-secondary">{t('totp.scan')}</p>
                <QrPanel value={flow.setupUrl!} label={t('totp.scan')} size={168} />
              </div>

              <div className="w-full space-y-2">
                <Label id="totp-enrollment-code-label" className="block text-center">
                  {t('totp.code')}
                </Label>
                <OtpInput
                  value={flow.code}
                  onChange={flow.setCode}
                  autoFocus
                  disabled={flow.isConfirming}
                  aria-labelledby="totp-enrollment-code-label"
                  onComplete={(next) => {
                    if (!flow.isConfirming) flow.confirmSetup(next);
                  }}
                />
                <p className="text-center text-caption text-fg-secondary">{t('totp.codeHint')}</p>
              </div>
            </div>
          ) : null}

          {showRecoveryStep ? (
            <div className="space-y-4 rounded-xl border border-border bg-surface-sunken/40 p-4">
              <div className="space-y-1">
                <p className="font-medium text-fg">{t('totp.recoveryTitle')}</p>
                <p className="text-body-sm text-fg-secondary">{t('totp.recoveryDescription')}</p>
              </div>
              <ul
                className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border bg-surface px-3 py-3 font-mono text-body-sm"
              >
                {flow.recoveryCodes?.map((item) => (
                  <li key={item} className="tabular-nums">{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {showManualStart ? (
            <p className="text-body-sm text-fg-secondary">{t('enrollmentRequired.description')}</p>
          ) : null}
        </DialogBody>

        <DialogFooter className="border-t border-border bg-surface-sunken/40 px-6 py-4">
          {dismissible ? (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
          ) : null}
          {showRecoveryStep ? (
            <Button onClick={flow.acknowledgeRecoveryCodes}>{t('totp.copyCodes')}</Button>
          ) : showCodeStep ? (
            <Button
              loading={flow.isConfirming}
              disabled={!canConfirm}
              onClick={() => flow.confirmSetup()}
            >
              {t('totp.confirm')}
            </Button>
          ) : showManualStart ? (
            <Button loading={flow.isStarting} onClick={() => flow.startSetup()}>
              {t('totp.setup')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
